/**
 * Ranking and congestion heuristics for the live overview — the "which PS now?"
 * logic. Pure functions over the generated API model, fully unit-tested.
 *
 * `QUEUE_LENGTH` from the feed is *people currently waiting* (a congestion
 * gauge, not cumulative accesses — see DECISIONS.md). So ranking by the lowest
 * total waiting answers "where would I be seen soonest right now?".
 */

import type { HospitalQueueState } from "@/api/generated/model";

export type CongestionLevel = "calm" | "moderate" | "busy" | "unknown";

/** Italian label per congestion level — shared by the overview cards and the detail hero. */
export const CONGESTION_LABEL: Record<CongestionLevel, string> = {
  calm: "Tranquillo",
  moderate: "Movimentato",
  busy: "Affollato",
  unknown: "Nessun dato",
};

/**
 * Display order for the overview, chosen by the user:
 *   - `asc`  — calmest (shortest queue) first; the default "which PS now?" view.
 *   - `desc` — busiest (longest queue) first.
 */
export type SortDirection = "asc" | "desc";

export interface RankedHospital {
  hospital: HospitalQueueState;
  /**
   * 1-based position among hospitals that have data, ascending by people
   * waiting (rank 1 = calmest). `null` for hospitals with no current data —
   * they can't be recommended, so they sort last.
   */
  rank: number | null;
  level: CongestionLevel;
}

/**
 * Absolute people-waiting thresholds for the per-card level chip. Provisional
 * heuristics (no per-hospital baseline yet); tune once history is banked. The
 * ranking below — which is relative, not threshold-based — is the primary
 * "where to go" signal; this chip is a secondary at-a-glance cue.
 */
const CALM_MAX = 4;
const MODERATE_MAX = 12;

export function congestionLevel(hospital: HospitalQueueState): CongestionLevel {
  if (!hospital.hasData) return "unknown";
  return congestionLevelFromCount(hospital.totalWaiting);
}

/**
 * Congestion level from a raw people-waiting count, sharing the same thresholds
 * as {@link congestionLevel}. Used by the detail hero, where the current total
 * is derived from the wait/history responses rather than a {@link HospitalQueueState}.
 * `null` (no current reading) → "unknown".
 */
export function congestionLevelFromCount(total: number | null): CongestionLevel {
  if (total === null || !Number.isFinite(total)) return "unknown";
  if (total <= CALM_MAX) return "calm";
  if (total <= MODERATE_MAX) return "moderate";
  return "busy";
}

/**
 * Order hospitals for the overview: those with data first, then the no-data
 * hospitals (which can never be recommended) preserving their incoming order.
 *
 * `rank` is always the calmest-first position (rank 1 = shortest queue), a stable
 * property of the hospital regardless of how the list is displayed. `direction`
 * only flips the *display* order: `desc` shows the busiest first while each card
 * keeps its calm-rank badge. Returns a new array; the input is not mutated.
 */
export function rankHospitals(
  hospitals: HospitalQueueState[],
  direction: SortDirection = "asc",
): RankedHospital[] {
  const ranked = hospitals
    .filter((h) => h.hasData)
    .sort((a, b) => a.totalWaiting - b.totalWaiting || a.nameIt.localeCompare(b.nameIt, "it"))
    .map((hospital, i) => ({
      hospital,
      rank: i + 1,
      level: congestionLevel(hospital),
    }));
  const ordered = direction === "desc" ? [...ranked].reverse() : ranked;
  const withoutData = hospitals.filter((h) => !h.hasData);

  return [
    ...ordered,
    ...withoutData.map((hospital) => ({
      hospital,
      rank: null,
      level: "unknown" as const,
    })),
  ];
}
